import { useTranslation } from "react-i18next";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/shared/components/ui/alert-dialog";
import { Spinner } from "@/shared/components/ui/spinner";

interface User {
  id: string;
  full_name: string | null;
  email: string;
}

interface DeleteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
  onConfirm: () => void;
  isDeleting: boolean;
  /**
   * `organization` (AB#84509): remover da organização deixou de apagar a conta — só
   * cai o vínculo com aquela organização. Anunciar "excluir usuário" descreveria uma
   * consequência que não acontece mais, e assusta quem só quer tirar alguém do time.
   * A exclusão de conta passou a existir num lugar só: o próprio perfil da pessoa.
   */
  mode?: 'delete' | 'unlink' | 'organization';
}

export const DeleteUserDialog = ({
  open,
  onOpenChange,
  user,
  onConfirm,
  isDeleting,
  mode = 'delete'
}: DeleteUserDialogProps) => {
  const { t } = useTranslation();

  const name = user?.full_name || user?.email;
  const labels = {
    unlink: {
      title: t("settings.users.unlinkUserTitle"),
      description: t("settings.users.unlinkUserDescription", { name }),
      confirm: t("settings.users.unlinkUserButton"),
    },
    organization: {
      title: t("settings.users.removeFromOrgTitle"),
      description: t("settings.users.removeFromOrgDescription", { name }),
      confirm: t("settings.users.removeFromOrgButton"),
    },
    delete: {
      title: t("settings.users.deleteUserTitle"),
      description: t("settings.users.deleteUserDescription", { name }),
      confirm: t("common.delete"),
    },
  }[mode];

  const { title, description, confirm: confirmLabel } = labels;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>
            {user && description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>
            {t("common.actions.cancel")}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
            disabled={isDeleting}
            className="bg-destructive hover:bg-destructive/90"
          >
            {isDeleting ? (
              <>
                <Spinner className="mr-2 h-4 w-4" />
                {t("common.deleting")}
              </>
            ) : (
              confirmLabel
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
